import { AlertTriangle, ArrowRight, Pin } from "lucide-react";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { fill } from "@/lib/i18n/fill";
import { formatDateOnly } from "@/domains/pm/utils/protocol";
import type { PendingScheduleChange } from "@/domains/pm/hooks/useScheduleChange";

/**
 * "This change affects 4 dependent activities and moves project completion by
 * 7 days."
 *
 * The cascade is deliberately not invisible. Moving a task that three others
 * wait on is a decision about the whole schedule, and a user who did not
 * realise that has been given a plan they never agreed to. So the change is
 * named before it lands — what moves, what refuses to move and why, and what
 * it does to the end date.
 *
 * "Keep current dates" is not a cancel. The edit the user just made still
 * applies; only the dependents stay where they are.
 */
export function ScheduleChangeDialog({
  pending,
  applying,
  onResolve,
  onCancel,
}: {
  pending: PendingScheduleChange | null;
  applying: boolean;
  onResolve: (cascade: boolean) => void;
  onCancel: () => void;
}) {
  const { t, language } = useLanguage();
  const copy = t.pm.issues.schedule;

  if (!pending) return null;

  const { moved, warnings, completion } = pending.preview;
  const day = (iso: string | null) => (iso ? formatDateOnly(iso, language) : "—");

  // A batch says "your changes", because there is no single edit to point at.
  const headline = pending.batch
    ? moved.length === 1
      ? copy.batchAffectsOne
      : fill(copy.batchAffectsMany, { count: String(moved.length) })
    : moved.length === 1
      ? copy.affectsOne
      : fill(copy.affectsMany, { count: String(moved.length) });

  const completionLine =
    completion.delta_days == null || completion.delta_days === 0
      ? copy.completionUnchanged
      : completion.delta_days > 0
        ? fill(copy.completionLater, { days: String(completion.delta_days) })
        : fill(copy.completionEarlier, { days: String(Math.abs(completion.delta_days)) });

  return (
    <AlertDialog open onOpenChange={(open) => !open && onCancel()}>
      <AlertDialogContent className="sm:max-w-[520px]">
        <AlertDialogHeader>
          <AlertDialogTitle>{moved.length > 0 ? headline : copy.nothingMoves}</AlertDialogTitle>
          <AlertDialogDescription>{completionLine}</AlertDialogDescription>
        </AlertDialogHeader>

        <div className="max-h-[280px] space-y-3 overflow-y-auto">
          {moved.length > 0 && (
            <div>
              <p className="mb-1 text-xs font-medium text-muted-foreground">{copy.movingLabel}</p>
              <ul className="space-y-1">
                {moved.map((m) => (
                  <li
                    key={m.issue_id}
                    className="flex items-center gap-2 rounded bg-muted/50 px-2 py-1 text-xs"
                  >
                    <span className="shrink-0 tabular-nums text-muted-foreground">
                      #{m.issue_number}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{m.title}</span>
                    <span className="shrink-0 whitespace-nowrap tabular-nums text-muted-foreground">
                      {day(m.old_start)}
                    </span>
                    <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground" />
                    <span className="shrink-0 whitespace-nowrap tabular-nums">
                      {day(m.new_start)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {warnings.length > 0 && (
            <div>
              <p className="mb-1 text-xs font-medium text-muted-foreground">
                {copy.notMovingLabel}
              </p>
              <ul className="space-y-1">
                {warnings.map((w) => (
                  <li
                    key={`${w.kind}-${w.issue_id}-${w.predecessor_issue_id ?? "x"}`}
                    className="flex items-start gap-2 rounded px-2 py-1 text-xs text-muted-foreground"
                  >
                    {w.kind === "pinned" ? (
                      <Pin className="mt-0.5 h-3 w-3 shrink-0" />
                    ) : (
                      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-[#d97706]" />
                    )}
                    <span>
                      {w.kind === "pinned"
                        ? fill(copy.pinnedWarning, {
                            n: String(w.issue_number),
                            title: w.title,
                          })
                        : fill(copy.softWarning, {
                            n: String(w.issue_number),
                            title: w.title,
                            date: day(w.required_start),
                          })}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Left-aligned: the dialog reads top-left to bottom-right, and the
            decision belongs under the list it is about rather than across the
            far corner from it. `sm:justify-start` overrides the footer's own
            default, which pins buttons right. */}
        <AlertDialogFooter className="sm:justify-start">
          {/* Not AlertDialogCancel: neither button cancels. The edit is
              applied either way — the choice is only whether the dependents
              follow it. */}
          <Button variant="outline" disabled={applying} onClick={() => onResolve(false)}>
            {copy.keepCurrentDates}
          </Button>
          <Button disabled={applying} onClick={() => onResolve(true)}>
            {copy.updateSchedule}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
